// RND-2: screen = (world - center) * scale + viewport/2. No sign flips (Y-down both spaces).
import { boundsValid, type Bounds, type Vec } from "../core/geometry";

export const BASE_PPMM = 96 / 25.4; // displayed 100% zoom ≈ true size on a 96 dpi screen
export const MIN_SCALE = 0.001;
export const MAX_SCALE = 1000;

export interface Camera {
  cx: number;
  cy: number;
  scale: number; // device-independent px per mm
}

export function createCamera(): Camera {
  return { cx: 0, cy: 0, scale: BASE_PPMM };
}

export function clampScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

export function worldToScreen(cam: Camera, vpW: number, vpH: number, p: Vec): Vec {
  return { x: (p.x - cam.cx) * cam.scale + vpW / 2, y: (p.y - cam.cy) * cam.scale + vpH / 2 };
}

export function screenToWorld(cam: Camera, vpW: number, vpH: number, p: Vec): Vec {
  return { x: (p.x - vpW / 2) / cam.scale + cam.cx, y: (p.y - vpH / 2) / cam.scale + cam.cy };
}

/** Zoom by `factor` keeping the world point under `screenPt` invariant. */
export function zoomAt(cam: Camera, vpW: number, vpH: number, screenPt: Vec, factor: number): Camera {
  const scale = clampScale(cam.scale * factor);
  if (scale === cam.scale) return cam;
  const anchor = screenToWorld(cam, vpW, vpH, screenPt);
  // Solve center so that anchor maps back to screenPt at the new scale.
  return {
    cx: anchor.x - (screenPt.x - vpW / 2) / scale,
    cy: anchor.y - (screenPt.y - vpH / 2) / scale,
    scale
  };
}

/** Pan by a screen-space delta. */
export function panBy(cam: Camera, dxScreen: number, dyScreen: number): Camera {
  return { cx: cam.cx - dxScreen / cam.scale, cy: cam.cy - dyScreen / cam.scale, scale: cam.scale };
}

/** Ctrl+0 zoom extents: fit bounds + 5% margin; invalid/empty bounds → home view. */
export function fitBounds(vpW: number, vpH: number, b: Bounds): Camera {
  if (!boundsValid(b) || vpW <= 0 || vpH <= 0) return { cx: 0, cy: 0, scale: BASE_PPMM };
  const w = Math.max(b.maxX - b.minX, 1e-3);
  const h = Math.max(b.maxY - b.minY, 1e-3);
  const scale = clampScale(Math.min(vpW / w, vpH / h) / 1.05);
  return { cx: (b.minX + b.maxX) / 2, cy: (b.minY + b.maxY) / 2, scale };
}

export function zoomPercent(cam: Camera): number {
  return (cam.scale / BASE_PPMM) * 100;
}
